package kz.dentalcrm.app.core.ui

import androidx.compose.animation.animateColor
import androidx.compose.animation.core.FastOutLinearInEasing
import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.animation.core.animateDp
import androidx.compose.animation.core.tween
import androidx.compose.animation.core.updateTransition
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.indication
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kz.dentalcrm.app.theme.DefaultTheme

private enum class SwitchState {
    IS_ENABLED,
    NOT_ENABLED
}

@Composable
fun UISwitch(
    modifier: Modifier = Modifier,
    enabledColor: Color = DefaultTheme.colors.primarySubduedSecond,
    disabledColor: Color = DefaultTheme.colors.surfaceSecondary,
    width: Dp = 40.dp,
    height: Dp = 24.dp,
    checked: Boolean = false,
    enabled: Boolean = false,
    onCheckedChange: (Boolean) -> Unit
) {
    val widthSize by remember {
        mutableStateOf(width)
    }
    val heightSize by remember {
        mutableStateOf(height)
    }
    var currentState = if (!checked) (SwitchState.NOT_ENABLED) else (SwitchState.IS_ENABLED)

    val bgDisabledColour by remember {
        mutableStateOf(disabledColor)
    }

    val bgEnabledColour by remember {
        mutableStateOf(enabledColor)
    }

    val transition = updateTransition(currentState, label = "SwitchState")
    val color by transition.animateColor(transitionSpec = {
        tween(200, easing = FastOutLinearInEasing)
    }, label = "") {
        when (it) {
            SwitchState.IS_ENABLED ->
                bgEnabledColour

            else -> bgDisabledColour
        }
    }
    val interactionSource = remember { MutableInteractionSource() }

    val clickable = Modifier.clickable(
        interactionSource = interactionSource,
        indication = null,
        enabled = enabled
    ) {
        currentState = if (currentState == SwitchState.NOT_ENABLED) {
            onCheckedChange(true)
            SwitchState.IS_ENABLED
        } else {
            onCheckedChange(false)
            SwitchState.NOT_ENABLED
        }
    }

    Box(
        modifier = Modifier
            .then(clickable)
            .indication(
                interactionSource = remember {
                    MutableInteractionSource()
                },
                indication = ripple(
                    bounded = true,
                    radius = 100.dp,
                    color = Color.Transparent
                )
            )
    ) {
        BoxWithConstraints(
            modifier = modifier
                .width(widthSize - 2.dp)
                .height(heightSize / 2)
                .indication(
                    interactionSource = remember {
                        MutableInteractionSource()
                    },
                    indication = null
                )
                .background(color = color, shape = RoundedCornerShape(100))
                .padding(horizontal = 1.dp),
            contentAlignment = Alignment.CenterStart
        ) {

            val roundCardSize = this.maxWidth / 2

            val xOffset by transition.animateDp(
                transitionSpec = {
                    tween(150, easing = LinearOutSlowInEasing)
                }, label = "xOffset"
            ) { state ->
                when (state) {
                    SwitchState.NOT_ENABLED -> 0.dp
                    SwitchState.IS_ENABLED -> this.maxWidth - roundCardSize
                }
            }
            Box(
                modifier = Modifier
                    .size(this.maxWidth / 2)
                    .offset(x = xOffset, y = 0.dp)
                    .padding(all = 2.dp)
                    .background(
                        color = DefaultTheme.colors.primary,
                        shape = RoundedCornerShape(100)
                    )
            )
        }
    }
}
